// Create / rename dialog for named resources, with server field errors.
import { useState, type SyntheticEvent, type ReactNode } from "react";
import { ApiProblem } from "@/api/errors";
import { Button } from "@/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/ui/dialog";
import { Field } from "@/ui/field";
import { Input } from "@/ui/input";

export function NameDialog({
  open,
  onOpenChange,
  title,
  description,
  label,
  initial = "",
  submitLabel,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  label: string;
  initial?: string;
  submitLabel: string;
  onSubmit: (name: string) => Promise<unknown>;
}) {
  const [name, setName] = useState(initial);
  const [error, setError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setPending(true);
    setError(undefined);
    try {
      await onSubmit(name.trim());
      onOpenChange(false);
    } catch (problem) {
      setError(problem instanceof ApiProblem ? (problem.fieldErrors().name ?? problem.message) : "No se pudo guardar.");
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setName(initial);
          setError(undefined);
        }
        onOpenChange(next);
      }}
    >
      <DialogContent title={title} description={description}>
        <form className="grid gap-4" onSubmit={(e) => void submit(e)}>
          <Field label={label} error={error}>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              maxLength={200}
              required
              autoFocus
            />
          </Field>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={pending || name.trim() === ""}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
